import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CornerUpLeft, Paperclip, SmilePlus, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageReactionSummary, MessageReply, ReactionEmoji } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * Reageren met een emoji en antwoorden op een bericht (BER-8), voor beide indelingen van een
 * gesprek: het bureauscherm en de telefoon.
 *
 * Eén reactie per persoon per bericht, uit een vaste set van zes. Een andere emoji kiezen vervangt
 * je reactie; dezelfde nog eens kiezen haalt hem weg. Dat is wat iedereen van WhatsApp kent, en de
 * teller blijft "hoeveel mensen" en niet "hoeveel tikken".
 *
 * Geen namen bij een reactie (ADR-0014): de server levert per emoji een aantal, meer niet.
 *
 * Dezelfde onderdelen dienen voor een gesprek binnen het bedrijf en een gesprek over bedrijven heen
 * (BER-8b): het is één chat, alleen de grens van wie erin mag verschilt.
 */

/** Wat een reactie-onderdeel van een bericht nodig heeft; beide soorten bericht hebben het. */
export interface Reactable {
  reactions: MessageReactionSummary[];
  myReaction: ReactionEmoji | null;
}

/** Wat de antwoordbalk van een bericht nodig heeft. */
export interface Quotable {
  senderDisplayName: string;
  body: string;
  attachments?: unknown[];
}

/** De volgorde van de kiezer. De server kent alleen de sleutel; het teken staat hier. */
export const REACTION_EMOJI: { key: ReactionEmoji; char: string }[] = [
  { key: 'thumbs_up', char: '👍' },
  { key: 'heart', char: '❤️' },
  { key: 'laugh', char: '😂' },
  { key: 'wow', char: '😮' },
  { key: 'sad', char: '😢' },
  { key: 'thanks', char: '🙏' },
];

const CHAR_OF = new Map(REACTION_EMOJI.map((emoji) => [emoji.key, emoji.char]));

export const emojiChar = (key: ReactionEmoji): string => CHAR_OF.get(key) ?? '👍';

/**
 * Reageren, een andere emoji kiezen of de reactie weghalen.
 *
 * Na afloop de berichtenlijsten verversen: het bureauscherm en de telefoon hebben elk hun eigen
 * querysleutel, en ze staan in dezelfde DOM. `shared` is een gesprek over bedrijven heen.
 */
export function useReactToMessage(conversationId: string, scope: 'tenant' | 'shared' = 'tenant') {
  const { api } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { messageId: string; emoji: ReactionEmoji | null }) => {
      const params = { path: { conversationId, messageId: input.messageId } };
      const body =
        input.emoji === null
          ? { reacted: false, emoji: 'thumbs_up' as const }
          : { reacted: true, emoji: input.emoji };
      const { error } =
        scope === 'shared'
          ? await api.PUT(
              '/api/v1/shared-conversations/{conversationId}/messages/{messageId}/reaction',
              { params, body },
            )
          : await api.PUT('/api/v1/conversations/{conversationId}/messages/{messageId}/reaction', {
              params,
              body,
            });
      if (error) throw error;
    },
    onSuccess: () => {
      for (const key of [['conversation'], ['messages'], ['shared-messages']]) {
        void queryClient.invalidateQueries({ queryKey: [...key, conversationId] });
      }
    },
  });
}

/** Wat een tik op een emoji betekent: dezelfde als de mijne haalt hem weg, een andere vervangt. */
export const nextReaction = (
  current: ReactionEmoji | null,
  picked: ReactionEmoji,
): ReactionEmoji | null => (current === picked ? null : picked);

/**
 * De zes emoji's op een rij.
 *
 * `onClose` sluit de kiezer bij Escape en bij een klik ernaast — zonder dat blijft hij boven het
 * volgende bericht hangen terwijl je verder leest.
 */
export function ReactionPicker({
  current,
  onPick,
  onClose,
  className,
}: {
  current: ReactionEmoji | null;
  onPick: (emoji: ReactionEmoji) => void;
  onClose?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onClose) return;
    const onPointer = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={t('messaging.react')}
      data-testid="reaction-picker"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-pill border border-border-light bg-surface p-1 shadow-md',
        className,
      )}
    >
      {REACTION_EMOJI.map((emoji) => (
        <button
          key={emoji.key}
          type="button"
          data-testid={`pick-${emoji.key}`}
          aria-pressed={current === emoji.key}
          aria-label={t(`messaging.reactionEmoji.${emoji.key}`)}
          onClick={() => onPick(emoji.key)}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-pill text-[20px] leading-none transition-transform hover:scale-125 hover:bg-background',
            current === emoji.key && 'bg-brand-primary-tint',
          )}
        >
          <span aria-hidden>{emoji.char}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * De reacties onder een ballon: per emoji een pill met het aantal.
 *
 * Een tik op een pill doet hetzelfde als die emoji in de kiezer: meedoen, wisselen of weghalen.
 * De mijne is gemarkeerd, zodat je ziet wat een tweede tik doet.
 */
export function ReactionPills({
  message,
  onReact,
  className,
}: {
  message: Reactable;
  onReact: (emoji: ReactionEmoji | null) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  if (message.reactions.length === 0) return null;

  return (
    <div
      data-testid="chat-reactions"
      className={cn('relative z-[1] flex flex-wrap gap-1', className)}
    >
      {message.reactions.map((reaction) => {
        const mine = message.myReaction === reaction.emoji;
        return (
          <button
            key={reaction.emoji}
            type="button"
            data-testid="chat-reaction"
            data-emoji={reaction.emoji}
            data-mine={mine ? 'true' : 'false'}
            aria-pressed={mine}
            aria-label={t('messaging.reactionPill', {
              emoji: t(`messaging.reactionEmoji.${reaction.emoji}`),
              count: reaction.count,
            })}
            onClick={() => onReact(nextReaction(message.myReaction, reaction.emoji))}
            className={cn(
              'inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 transition-colors',
              mine
                ? 'border-brand-primary bg-brand-primary-tint'
                : 'border-border bg-surface hover:border-brand-primary',
            )}
          >
            <span aria-hidden className="text-[13px] leading-none">
              {emojiChar(reaction.emoji)}
            </span>
            <span className="font-heading text-[10px] font-semibold text-text-secondary">
              {reaction.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Het citaat bovenaan een ballon: wie het oorspronkelijke bericht schreef, en het begin ervan.
 *
 * Een tik springt naar dat bericht als het op deze pagina staat. Is het verwijderd, dan zegt het
 * citaat dat — het antwoord zelf blijft staan.
 */
export function ReplyQuote({
  reply,
  mine,
  onJump,
}: {
  reply: MessageReply;
  mine: boolean;
  onJump?: (messageId: string) => void;
}) {
  const { t } = useTranslation();
  const text = reply.available
    ? reply.body || (reply.hasAttachments ? t('messaging.quoteAttachment') : '')
    : t('messaging.quoteDeleted');

  return (
    <button
      type="button"
      data-testid="chat-reply-quote"
      disabled={!reply.available || !onJump}
      onClick={() => onJump?.(reply.id)}
      className={cn(
        'mb-1.5 block w-full min-w-0 rounded-[10px] border-l-[3px] px-2.5 py-1.5 text-left',
        mine
          ? 'border-white/70 bg-white/15 text-text-on-dark'
          : 'border-brand-primary bg-background text-text-primary',
        reply.available && onJump && 'cursor-pointer hover:opacity-90',
      )}
    >
      <span className="block truncate font-heading text-caption font-semibold">
        {reply.senderDisplayName}
      </span>
      <span
        className={cn(
          'flex items-center gap-1 truncate text-caption',
          mine ? 'text-text-on-dark opacity-80' : 'text-text-secondary',
          !reply.available && 'italic',
        )}
      >
        {reply.available && reply.hasAttachments && !reply.body && (
          <Paperclip aria-hidden className="h-3 w-3 shrink-0" />
        )}
        <span className="truncate">{text}</span>
      </span>
    </button>
  );
}

/** De balk boven het invoerveld zolang je op een bericht antwoordt. */
export function ReplyComposerBar({
  message,
  onCancel,
  className,
}: {
  message: Quotable;
  onCancel: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const text =
    message.body || ((message.attachments?.length ?? 0) > 0 ? t('messaging.quoteAttachment') : '');

  return (
    <div
      data-testid="reply-composer"
      className={cn(
        'flex items-center gap-2 rounded-[12px] border-l-[3px] border-brand-primary bg-background px-3 py-2',
        className,
      )}
    >
      <CornerUpLeft aria-hidden className="h-4 w-4 shrink-0 text-brand-primary" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading text-caption font-semibold text-text-primary">
          {t('messaging.replyingTo', { name: message.senderDisplayName })}
        </p>
        <p className="truncate text-caption text-text-secondary">{text}</p>
      </div>
      <button
        type="button"
        data-testid="cancel-reply"
        aria-label={t('messaging.cancelReply')}
        onClick={onCancel}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill text-text-secondary hover:bg-surface hover:text-brand-primary"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>
    </div>
  );
}

/** De twee knoppen naast een ballon op het bureauscherm: reageren en antwoorden. */
export function MessageActionButtons({
  onOpenPicker,
  onReply,
  pickerOpen,
}: {
  onOpenPicker: () => void;
  onReply: () => void;
  pickerOpen: boolean;
}) {
  const { t } = useTranslation();
  const button =
    'flex h-8 w-8 items-center justify-center rounded-pill text-text-secondary hover:bg-surface hover:text-brand-primary';

  return (
    <>
      <button
        type="button"
        data-testid="message-react"
        aria-label={t('messaging.react')}
        aria-expanded={pickerOpen}
        onClick={onOpenPicker}
        className={button}
      >
        <SmilePlus aria-hidden className="h-[17px] w-[17px]" />
      </button>
      <button
        type="button"
        data-testid="message-reply"
        aria-label={t('messaging.reply')}
        onClick={onReply}
        className={button}
      >
        <CornerUpLeft aria-hidden className="h-[17px] w-[17px]" />
      </button>
    </>
  );
}

/**
 * Springt naar een bericht in de lijst en laat het even oplichten.
 *
 * Staat het niet op de geladen pagina, dan gebeurt er niets: liever geen sprong dan een sprong naar
 * de verkeerde plek.
 */
export function jumpToMessage(container: HTMLElement | null, messageId: string): void {
  const target = container?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
  if (!target) return;
  target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  target.classList.add('chat-flash');
  window.setTimeout(() => target.classList.remove('chat-flash'), 1200);
}
